import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Code,
  Cpu,
  Globe,
  GraduationCap,
  Heart,
  Layers,
  Lock,
  MessageSquare,
  PackageCheck,
  Palette,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import logger from "../../utils/logger.js";
import "./HomeRedesign.css";
import "../FreelancerFiverr.css";
import {
  formatServiceCurrency,
  getFreelancerServiceTitle,
  getServiceFreelancer,
  listFreelancerServices,
  getFreelancerName,
} from "../../api/freelancerServices";
import { toAbsoluteUrl, handleImageFallback } from "../../utils/imageUtils";
import api from "../../api/client";

const serviceFallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80";

const courseFallbackImage =
  "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1200&q=80";

export default function Home() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [featuredServices, setFeaturedServices] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadData() {
      try {
        setLoading(true);

        // Fetch top freelancer services
        const servicesData = await listFreelancerServices({ limit: 6 }).catch(() => []);
        if (active) setFeaturedServices(Array.isArray(servicesData) ? servicesData.slice(0, 6) : []);

        // Fetch top courses
        const coursesRes = await api.apiGet("courses?limit=3").catch(() => null);
        const coursesData = coursesRes?.courses || coursesRes?.data || [];
        if (active) setFeaturedCourses(Array.isArray(coursesData) ? coursesData.slice(0, 3) : []);
      } catch (err) {
        logger.error("Failed to load home page showcase data", err);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, []);

  function handleSearchSubmit(e) {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/freelancers?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate("/freelancers");
    }
  }

  // Preset categories
  const CATEGORIES = [
    { icon: "🎨", labelAr: "التصميم والرسوميات", labelEn: "Graphics & Design", category: "Design" },
    { icon: "💻", labelAr: "التطوير والبرمجة", labelEn: "Web & Tech", category: "Development" },
    { icon: "🚀", labelAr: "التسويق الرقمي", labelEn: "Digital Marketing", category: "Marketing" },
    { icon: "✍️", labelAr: "الكتابة والترجمة", labelEn: "Writing & Translation", category: "Writing" },
    { icon: "🤖", labelAr: "خدمات الذكاء الاصطناعي", labelEn: "AI Services", category: "AI" },
  ];

  return (
    <div className="home-redesign-page" dir={isAr ? "rtl" : "ltr"}>
      {/* 1. HERO SHOWCASE SECTION */}
      <section className="home-hero">
        <div className="home-container">
          <div className="text-center mx-auto" style={{ maxWidth: "860px" }}>
            <div className="home-hero__badge">
              <Sparkles size={16} />
              <span>
                {isAr
                  ? "المنصة الشاملة للمستقلين، البرمجة، والحلول التقنية"
                  : "All-In-One Platform for Freelancers & Tech Growth"}
              </span>
            </div>

            <h1 className="home-hero__title">
              {isAr ? (
                <>
                  اختر أفضل <span>المستقلين والخبرات التقنية</span> لتنفيذ مشاريعك
                </>
              ) : (
                <>
                  Find Top <span>Freelancers & Tech Experts</span> for Your Business
                </>
              )}
            </h1>

            <p className="home-hero__subtitle mx-auto">
              {isAr
                ? "تواصل مع مستقلين محترفين، انضم لأفضل الكورسات والمسارات التقنية، واستفد من نظام حماية الضمان Escrow المالي المباشر."
                : "Connect with verified freelancers, enroll in expert software courses, and benefit from 100% Escrow financial protection."}
            </p>

            {/* Live Hero Search Box */}
            <form onSubmit={handleSearchSubmit} className="home-hero__search-box mx-auto">
              <Search size={20} className="text-muted flex-shrink-0" />
              <input
                type="text"
                className="home-hero__search-input"
                placeholder={
                  isAr
                    ? "ابحث عن خدمة، برمجة تطبيق، تصميم شعار، أو كورس..."
                    : "Search for web design, app development, logo, or course..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="home-hero__search-btn">
                <span>{isAr ? "بحث" : "Search"}</span>
                <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
              </button>
            </form>

            {/* Category Chips Bar */}
            <div className="home-hero__chips justify-content-center">
              <span className="small text-muted font-bold text-uppercase me-2">
                {isAr ? "الأقسام الشائعة:" : "Popular:"}
              </span>
              {CATEGORIES.map((cat, idx) => (
                <Link key={idx} to={`/freelancers?category=${cat.category}`} className="home-hero__chip">
                  <span>{cat.icon}</span>
                  <span>{isAr ? cat.labelAr : cat.labelEn}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. STATS & TRUST BAR */}
      <section className="home-stats">
        <div className="home-container">
          <div className="home-stats__grid">
            <div className="home-stat-card">
              <div className="home-stat-card__icon">
                <PackageCheck size={26} />
              </div>
              <div>
                <div className="home-stat-card__number">+5,000</div>
                <div className="home-stat-card__label">{isAr ? "خدمة متميزة منجزة" : "Completed Service Orders"}</div>
              </div>
            </div>

            <div className="home-stat-card">
              <div className="home-stat-card__icon">
                <ShieldCheck size={26} />
              </div>
              <div>
                <div className="home-stat-card__number">99.8%</div>
                <div className="home-stat-card__label">{isAr ? "نسبة الأمان بضمان Escrow" : "Escrow Financial Protection"}</div>
              </div>
            </div>

            <div className="home-stat-card">
              <div className="home-stat-card__icon">
                <GraduationCap size={26} />
              </div>
              <div>
                <div className="home-stat-card__number">+120</div>
                <div className="home-stat-card__label">{isAr ? "كورس ومسار تدريبي" : "Tech Courses & Tracks"}</div>
              </div>
            </div>

            <div className="home-stat-card">
              <div className="home-stat-card__icon">
                <Users size={26} />
              </div>
              <div>
                <div className="home-stat-card__number">+15,000</div>
                <div className="home-stat-card__label">{isAr ? "مستخدم ومستقل موثق" : "Verified Active Members"}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FEATURED FREELANCER GIGS SHOWCASE */}
      <section className="home-section">
        <div className="home-container">
          <div className="home-section-header">
            <div>
              <h2 className="home-section-header__title">
                {isAr ? "أبرز خدمات المستقلين المتاحة" : "Featured Freelancer Services"}
              </h2>
              <p className="home-section-header__subtitle">
                {isAr
                  ? "تصفح خدمات عالي الجودة يقدمها أفضل المستقلين مع ضمان التسليم الكامل."
                  : "Hand-picked high quality services offered by top rated verified freelancers."}
              </p>
            </div>
            <Link to="/freelancers" className="home-section-header__btn">
              <span>{isAr ? "عرض جميع الخدمات" : "Explore All Gigs"}</span>
              <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
            </Link>
          </div>

          <div className="home-grid">
            {featuredServices.length > 0
              ? featuredServices.map((service) => {
                  const title = getFreelancerServiceTitle(service, isAr);
                  const freelancer = getServiceFreelancer(service);
                  const freelancerName = getFreelancerName(freelancer, isAr);
                  const rawAvatar = freelancer?.avatar_url || freelancer?.avatar || freelancer?.image;
                  const avatarUrl = toAbsoluteUrl(rawAvatar);
                  const coverImage = toAbsoluteUrl(service.cover_image) || serviceFallbackImage;
                  const price = service.price || 40;
                  const detailRef = service.slug || service.id;

                  return (
                    <article key={service.id} className="fv-gig-card">
                      <Link to={`/freelancers/${detailRef}`} className="fv-gig-card__media" aria-label={title}>
                        <img
                          src={coverImage}
                          alt={title}
                          className="fv-gig-card__image"
                          onError={(e) => handleImageFallback(e, serviceFallbackImage)}
                        />
                        <span className="fv-gig-badge fv-badge-pro">
                          <Sparkles size={12} />
                          <span>Pro Verified</span>
                        </span>
                      </Link>
                      <div className="fv-gig-card__body">
                        <div className="fv-gig-card__seller">
                          <div className="fv-gig-card__avatar-wrap">
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt={freelancerName}
                                className="fv-gig-card__avatar"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback = e.currentTarget.parentElement?.querySelector(".fv-gig-card__avatar-fallback");
                                  if (fallback) fallback.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="fv-gig-card__avatar-fallback"
                              style={{ display: avatarUrl ? "none" : "flex" }}
                            >
                              {freelancerName.charAt(0).toUpperCase() || "F"}
                            </div>
                          </div>
                          <div className="fv-gig-card__seller-info">
                            <span className="fv-gig-card__seller-name">{freelancerName}</span>
                          </div>
                        </div>
                        <h3 className="fv-gig-card__title">
                          <Link to={`/freelancers/${detailRef}`}>{title}</Link>
                        </h3>
                        <div className="fv-gig-card__rating-row">
                          <Star size={14} className="fv-gig-card__star" />
                          <strong className="fv-gig-card__score">4.9</strong>
                          <span className="fv-gig-card__count">(120)</span>
                        </div>
                        <div className="fv-gig-card__footer">
                          <div className="fv-gig-card__price-wrap">
                            <span className="fv-gig-card__price-label">{isAr ? "تبدأ من" : "Starting at"}</span>
                            <span className="fv-gig-card__price-value">{formatServiceCurrency(price, "USD")}</span>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })
              : [1, 2, 3].map((idx) => (
                  <article key={idx} className="fv-gig-card">
                    <Link to="/freelancers" className="fv-gig-card__media">
                      <img src={serviceFallbackImage} alt="Demo Gig" className="fv-gig-card__image" />
                      <span className="fv-gig-badge fv-badge-pro">
                        <Sparkles size={12} />
                        <span>Pro Verified</span>
                      </span>
                    </Link>
                    <div className="fv-gig-card__body">
                      <div className="fv-gig-card__seller">
                        <div className="fv-gig-card__avatar-wrap">
                          <div className="fv-gig-card__avatar-fallback">M</div>
                        </div>
                        <div className="fv-gig-card__seller-info">
                          <span className="fv-gig-card__seller-name">{isAr ? "أحمد حسونة" : "Ahmed Hasouna"}</span>
                        </div>
                      </div>
                      <h3 className="fv-gig-card__title">
                        <Link to="/freelancers">
                          {isAr ? "تطوير وتصميم موقع إلكتروني متكامل عالي السرعة" : "Full Stack Modern Web Application Development"}
                        </Link>
                      </h3>
                      <div className="fv-gig-card__rating-row">
                        <Star size={14} className="fv-gig-card__star" />
                        <strong className="fv-gig-card__score">4.9</strong>
                        <span className="fv-gig-card__count">(85)</span>
                      </div>
                      <div className="fv-gig-card__footer">
                        <div className="fv-gig-card__price-wrap">
                          <span className="fv-gig-card__price-label">{isAr ? "تبدأ من" : "Starting at"}</span>
                          <span className="fv-gig-card__price-value">$50</span>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
          </div>
        </div>
      </section>

      {/* 4. WHY CHOOSE MUSA GROUP / ESCROW ADVANTAGES */}
      <section className="home-section home-section--alt">
        <div className="home-container">
          <div className="text-center mx-auto mb-5" style={{ maxWidth: "680px" }}>
            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill font-bold mb-2">
              <ShieldCheck size={16} className="me-1" />
              {isAr ? "ضمان مالي 100%" : "100% Financial Escrow Protection"}
            </span>
            <h2 className="home-section-header__title m-0">
              {isAr ? "لماذا تختار منصة Musa Group؟" : "Why Work With Musa Group?"}
            </h2>
            <p className="home-section-header__subtitle mt-2">
              {isAr
                ? "نوفر بيئة عمل وحلول متكاملة تضمن حق العميل والمستقل في كل خطوة."
                : "We provide an integrated, safe ecosystem ensuring complete rights for buyers and sellers."}
            </p>
          </div>

          <div className="home-features-grid">
            <div className="home-feature-card">
              <div className="home-feature-card__icon">
                <Lock size={26} />
              </div>
              <h3 className="home-feature-card__title">
                {isAr ? "نظام حماية الضمان Escrow" : "Escrow Financial Protection"}
              </h3>
              <p className="home-feature-card__desc">
                {isAr
                  ? "يتم الاحتفاظ بأموال الطلب في حساب الضمان حتى تقوم بمعاينة العمل النهائي والموافقة الكاملة عليه بنفسك."
                  : "Your funds are securely held in Escrow until you review and approve the final work delivery."}
              </p>
            </div>

            <div className="home-feature-card">
              <div className="home-feature-card__icon">
                <MessageSquare size={26} />
              </div>
              <h3 className="home-feature-card__title">
                {isAr ? "تواصل مباشر قبل الطلب" : "Direct Pre-Order Inquiry"}
              </h3>
              <p className="home-feature-card__desc">
                {isAr
                  ? "تحدث مع المستقل وناقش تفاصيل وميزانية مشروعك مباشرة قبل دفع أي مبلغ من خلال نافذة الاستفسار المباشرة."
                  : "Message freelancers and discuss custom quotes and scopes before placing an order."}
              </p>
            </div>

            <div className="home-feature-card">
              <div className="home-feature-card__icon">
                <CheckCircle2 size={26} />
              </div>
              <h3 className="home-feature-card__title">
                {isAr ? "مستقلون ومدرّبون موثقون" : "Verified Experts & Trainers"}
              </h3>
              <p className="home-feature-card__desc">
                {isAr
                  ? "نخبة متميزة من المستقلين والمدربين المعتمدين بمهارات ومشاريع تم التحقق من جودتها من قبل المنصة."
                  : "Vetted professional freelancers and certified instructors with top customer reviews."}
              </p>
            </div>

            <div className="home-feature-card">
              <div className="home-feature-card__icon">
                <Rocket size={26} />
              </div>
              <h3 className="home-feature-card__title">
                {isAr ? "سرعة تسليم ودعم 24/7" : "Fast Delivery & 24/7 Support"}
              </h3>
              <p className="home-feature-card__desc">
                {isAr
                  ? "عداد تنازلي للتسليم السريع مع فريق دعم فني متواجد على مدار الساعة لمساعدتك في أي استفسار."
                  : "Live countdown timers and round-the-clock support for seamless project execution."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. FEATURED COURSES SHOWCASE */}
      <section className="home-section">
        <div className="home-container">
          <div className="home-section-header">
            <div>
              <h2 className="home-section-header__title">
                {isAr ? "الكورسات والمسارات التقنية المميزة" : "Top Courses & Training Tracks"}
              </h2>
              <p className="home-section-header__subtitle">
                {isAr
                  ? "طوّر مهاراتك وانضم لأحدث الكورسات التطبيقية المباشرة في البرمجة والتكنولوجيا."
                  : "Upgrade your skills with hands-on software development and tech courses."}
              </p>
            </div>
            <Link to="/courses" className="home-section-header__btn">
              <span>{isAr ? "استكشف جميع الكورسات" : "View All Courses"}</span>
              <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
            </Link>
          </div>

          <div className="row g-4">
            {featuredCourses.length > 0
              ? featuredCourses.map((course) => {
                  const courseImg = toAbsoluteUrl(course.image || course.cover_image) || courseFallbackImage;
                  return (
                    <div key={course.id} className="col-lg-4 col-md-6">
                      <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100 bg-body">
                        <img
                          src={courseImg}
                          alt={course.title_ar || course.title}
                          className="object-fit-cover w-100"
                          style={{ height: "200px" }}
                          onError={(e) => handleImageFallback(e, courseFallbackImage)}
                        />
                        <div className="p-4 d-flex flex-column justify-content-between flex-grow-1">
                          <div>
                            <span className="badge bg-primary bg-opacity-10 text-primary font-bold mb-2">
                              {isAr ? "دورة تدريبية" : "Interactive Course"}
                            </span>
                            <h3 className="fw-bold fs-5 mb-2 text-body">
                              {course.title_ar && isAr ? course.title_ar : course.title || "Software Engineering Course"}
                            </h3>
                          </div>
                          <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                            <strong className="text-success fs-5 font-bold">
                              {formatServiceCurrency(course.price || 99, "USD")}
                            </strong>
                            <Link to={`/courses/${course.id}`} className="fv-search-box__btn rounded-3 px-3 py-1 text-decoration-none">
                              <span>{isAr ? "عرض التفاصيل" : "View Course"}</span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              : [1, 2, 3].map((idx) => (
                  <div key={idx} className="col-lg-4 col-md-6">
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100 bg-body">
                      <img src={courseFallbackImage} alt="Demo Course" className="object-fit-cover w-100" style={{ height: "200px" }} />
                      <div className="p-4 d-flex flex-column justify-content-between flex-grow-1">
                        <div>
                          <span className="badge bg-primary bg-opacity-10 text-primary font-bold mb-2">
                            {isAr ? "تطوير البرمجيات" : "Software Engineering"}
                          </span>
                          <h3 className="fw-bold fs-5 mb-2 text-body">
                            {isAr ? "دورة احترافية في تطوير تطبيقات React & Laravel" : "Professional Full Stack React & Laravel Masterclass"}
                          </h3>
                        </div>
                        <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                          <strong className="text-success fs-5 font-bold">$99</strong>
                          <Link to="/courses" className="fv-search-box__btn rounded-3 px-3 py-1 text-decoration-none">
                            <span>{isAr ? "عرض الكورس" : "View Course"}</span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* 6. REMOTE TECH CAREERS & JOBS BANNER */}
      <section className="home-section pt-0">
        <div className="home-container">
          <div className="home-jobs-banner">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <span className="badge bg-success text-white font-bold px-3 py-2 rounded-pill mb-3">
                  <Briefcase size={14} className="me-1" />
                  {isAr ? "فرص عمل وتوظيف تقني" : "Remote Tech Careers"}
                </span>
                <h2 className="home-jobs-banner__title">
                  {isAr ? "هل تبحث عن فرصة عمل تقنية مميزة؟" : "Looking for Your Next Tech Career Opportunity?"}
                </h2>
                <p className="home-jobs-banner__desc">
                  {isAr
                    ? "استكشف آلاف الوظائف التقنية عن بُعد وفي الشركات الرائدة، وقدّم طلبك مباشرة من خلال المنصة."
                    : "Explore hundreds of remote and full-time tech job opportunities with top engineering companies."}
                </p>
                <Link to="/jobs" className="fv-search-box__btn rounded-pill px-4 py-3 d-inline-flex text-decoration-none">
                  <span>{isAr ? "تصفح الوظائف المتاحة" : "Browse Open Jobs"}</span>
                  <ArrowRight size={18} className={isAr ? "rotate-180 ms-2" : "ms-2"} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
